import { environment } from '../config';
import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import axios from 'axios';

const API = environment.apiUrl;

const STEPS = [
  { id: 'welcome',     label: 'Welcome' },
  { id: 'personal',   label: 'Personal Info' },
  { id: 'payroll',    label: 'Payroll' },
  { id: 'email',      label: 'Work Email' },
  { id: 'contract',   label: 'Contract' },
  { id: 'acknowledgments', label: 'Agreements' },
  { id: 'training',   label: 'Training Plan' },
  { id: 'done',       label: 'Complete' },
];

const SHIRT_SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'];
const SHOE_SIZES = ['7', '7.5', '8', '8.5', '9', '9.5', '10', '10.5', '11', '11.5', '12', '12.5', '13', '14', '15'];
const ROLE_LABELS: Record<string, string> = {
  salesman: 'Field Sales Representative',
  estimator: 'Estimator',
  foreman: 'Field Foreman',
  admin: 'Administrator',
  contracting_officer: 'Contracting Officer',
};

export default function OnboardingPortal() {
  const { token } = useParams<{ token: string }>();
  const [invite, setInvite] = useState<any>(null);
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [contractHTML, setContractHTML] = useState('');
  const [trainingModules, setTrainingModules] = useState<any[]>([]);
  const sigRef = useRef<any>(null);

  // Form state
  const [form, setForm] = useState({
    first_name: '', last_name: '', preferred_name: '', date_of_birth: '',
    phone: '', address: '', city: '', state: 'DE', zip: '',
    shoe_size: '', shirt_size: '',
    emergency_name: '', emergency_phone: '', emergency_relationship: '',
    dl_number: '', dl_state: 'DE', dl_expiry: '',
    work_email_requested: '',
    ssn: '', bank_routing: '', bank_account: '', bank_type: 'checking',
    contract_signed: false, contract_signature: '',
    handbook_acknowledged: false, sms_consent: false,
    drug_policy: false, bg_check: false,
  });
  const [emailAvailable, setEmailAvailable] = useState<boolean | null>(null);
  const [emailChecking, setEmailChecking] = useState(false);
  const [dlFile, setDlFile] = useState<File | null>(null);
  const [signatureData, setSignatureData] = useState('');

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/onboarding/token/${token}`)
      .then(r => {
        setInvite(r.data.invite);
        const inviteNameParts = r.data.invite.name.trim().split(/\s+/);
        setForm(f => ({
          ...f,
          first_name: inviteNameParts[0] || '',
          last_name: inviteNameParts.slice(1).join(' '),
        }));
        // Generate email suggestion
        const nameParts = r.data.invite.name.toLowerCase().split(' ');
        const suggested = nameParts[0] + '@mcmullenbrowne.com';
        setForm(f => ({ ...f, work_email_requested: suggested }));
      })
      .catch(e => setError(e.response?.data?.error || 'Invalid or expired link'))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    if (!token || step < 3) return;
    if (step === 4 && !contractHTML) {
      axios.get(`${API}/onboarding/contract/${token}`)
        .then(r => setContractHTML(r.data.html)).catch(() => {});
    }
    if (step === 6 && trainingModules.length === 0) {
      axios.get(`${API}/onboarding/training/${token}`)
        .then(r => setTrainingModules(r.data.modules)).catch(() => {});
    }
  }, [step, token]);

  const checkEmail = async (email: string) => {
    if (!email.includes('@')) return;
    const full = email.includes('@') ? email : `${email}@mcmullenbrowne.com`;
    setEmailChecking(true);
    try {
      const r = await axios.post(`${API}/onboarding/check-email`, { email: full });
      setEmailAvailable(r.data.available);
    } catch { setEmailAvailable(null); }
    finally { setEmailChecking(false); }
  };

  const saveProgress = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/onboarding/submit/${token}`, {
        ...form,
        legal_name: `${form.first_name.trim()} ${form.last_name.trim()}`.trim(),
        contract_signature_image: signatureData,
      });
    } catch (e) { console.warn('Save progress failed:', e); }
    finally { setSaving(false); }
  };

  const [validationError, setValidationError] = useState('');

  const validateStep = (): string => {
    const s = STEPS[step].id;
    if (s === 'personal') {
      if (!form.first_name.trim()) return 'Legal first name is required';
      if (!form.last_name.trim()) return 'Legal last name is required';
      if (!form.date_of_birth) return 'Date of birth is required';
      if (!form.phone.trim()) return 'Phone number is required';
      if (!form.address.trim()) return 'Home address is required';
      if (!form.city.trim()) return 'City is required';
      if (!form.zip.trim()) return 'ZIP code is required';
      if (!form.shirt_size) return 'Shirt size is required';
      if (!form.shoe_size) return 'Shoe size is required';
      if (!form.emergency_name.trim()) return 'Emergency contact name is required';
      if (!form.emergency_phone.trim()) return 'Emergency contact phone is required';
      if (!form.emergency_relationship.trim()) return 'Emergency contact relationship is required';
      if (!form.dl_number.trim()) return 'Driver\'s license number is required';
      if (!form.dl_expiry) return 'Driver\'s license expiration date is required';
      if (!dlFile) return 'Driver\'s license photo is required — please upload a photo';
    }
    if (s === 'payroll') {
      if (!form.ssn.trim()) return 'Social Security Number is required';
      if (!form.bank_routing.trim()) return 'Bank routing number is required';
      if (!form.bank_account.trim()) return 'Bank account number is required';
    }
    if (s === 'email') {
      if (!form.work_email_requested.trim()) return 'Work email is required';
      if (!form.work_email_requested.endsWith('@mcmullenbrowne.com')) return 'Email must end in @mcmullenbrowne.com';
      if (emailAvailable === false) return 'That email is already taken — please choose another';
    }
    if (s === 'contract') {
      if (!signatureData) return 'Please sign the contract before continuing';
      if (!form.contract_signed) return 'Please check the agreement box to confirm you have read and agree to the contract';
    }
    if (s === 'acknowledgments') {
      if (!form.handbook_acknowledged) return 'Please acknowledge the Employee Handbook';
      if (!form.drug_policy) return 'Please acknowledge the Drug-Free Workplace Policy';
      if (!form.bg_check) return 'Please authorize the background check';
    }
    return '';
  };

  const handleNext = async () => {
    const err = validateStep();
    if (err) { setValidationError(err); window.scrollTo(0, 0); return; }
    setValidationError('');
    setSaving(true);
    try {
      await saveProgress();
      // Upload DL photo when leaving the personal info step
      if (STEPS[step].id === 'personal' && dlFile) {
        const formData = new FormData();
        formData.append('file', dlFile);
        await axios.post(`${API}/onboarding/upload-dl/${token}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        }).catch(e => console.warn('DL upload failed:', e.message));
      }
    } finally { setSaving(false); }
    setStep(s => Math.min(s + 1, STEPS.length - 1));
    window.scrollTo(0, 0);
  };

  const handleComplete = async () => {
    setSaving(true);
    try {
      // Always save progress first, then mark complete
      await axios.post(`${API}/onboarding/submit/${token}`, {
        ...form,
        legal_name: `${form.first_name.trim()} ${form.last_name.trim()}`.trim(),
        contract_signed: true,
        sms_consent: form.sms_consent,
        handbook_acknowledged: form.handbook_acknowledged,
        contract_signature_image: signatureData,
      });
      // Generate signed contract PDF
      await axios.post(`${API}/onboarding/generate-contract/${token}`, {
        signature_image: signatureData,
        employee_name: `${form.first_name.trim()} ${form.last_name.trim()}`.trim() || invite?.name,
      }).catch(e => console.warn('Contract PDF:', e.message));
      await axios.post(`${API}/onboarding/complete/${token}`);
      setStep(STEPS.length - 1);
    } catch (e: any) {
      setError('Failed to complete onboarding. Please try again.');
    } finally { setSaving(false); }
  };

  const f = (key: string, val: any) => { setValidationError(''); setForm(prev => ({ ...prev, [key]: val })); };

  if (loading) return <Screen><div style={styles.loading}>Loading your onboarding...</div></Screen>;
  if (error) return <Screen><div style={styles.errorBox}>{error}</div></Screen>;
  if (!invite) return null;

  // ─── Step Renders ────────────────────────────────────────────────────────

  if (STEPS[step].id === 'welcome') return (
    <Screen>
      <div style={styles.hero}>
        <img src="/logo-white.png" alt="McMullen Browne" style={{ height: 44, width: 'auto', display: 'block', margin: '0 auto 10px' }} />
        <div style={styles.heroSub}>Employee Onboarding</div>
      </div>
      <div style={styles.card}>
        <h2 style={styles.cardTitle}>Welcome, {invite.name}! 👋</h2>
        <p style={styles.cardText}>You've been invited to join McMullen Browne Development LLC as a <strong>{ROLE_LABELS[invite.role] || invite.role}</strong>.</p>
        <p style={styles.cardText}>This process takes about <strong>20–30 minutes</strong>. You'll complete your:</p>
        <ul style={styles.list}>
          <li>Personal information & payroll setup</li>
          <li>Work email selection</li>
          <li>Employment contract (digital signature)</li>
          <li>Policy acknowledgments & SMS consent</li>
          <li>First-week training schedule preview</li>
        </ul>
        <p style={styles.cardText}>Have your driver's license and bank information ready.</p>
        <Btn onClick={() => setStep(1)}>Get Started →</Btn>
      </div>
    </Screen>
  );

  if (STEPS[step].id === 'personal') return (
    <Screen step={step}>
      <SectionTitle>Personal Information</SectionTitle>
      {validationError && <ValidationError message={validationError} />}
      <div style={styles.card}>
        <Grid>
          <Field label="Legal First Name *" value={form.first_name} onChange={v => f('first_name', v)} placeholder="First name" />
          <Field label="Legal Last Name *" value={form.last_name} onChange={v => f('last_name', v)} placeholder="Last name" />
          <Field label="Preferred Name" value={form.preferred_name} onChange={v => f('preferred_name', v)} placeholder="What you like to be called" />
          <Field label="Date of Birth *" value={form.date_of_birth} onChange={v => f('date_of_birth', v)} type="date" />
          <Field label="Phone Number *" value={form.phone} onChange={v => f('phone', v)} type="tel" placeholder="(302) 555-5555" />
        </Grid>
        <Field label="Home Address *" value={form.address} onChange={v => f('address', v)} placeholder="Street address" full />
        <Grid>
          <Field label="City *" value={form.city} onChange={v => f('city', v)} />
          <Field label="State" value={form.state} onChange={v => f('state', v)} />
          <Field label="ZIP Code *" value={form.zip} onChange={v => f('zip', v)} />
        </Grid>
        <Grid>
          <SelectField label="Shirt Size *" value={form.shirt_size} options={SHIRT_SIZES} onChange={v => f('shirt_size', v)} />
          <SelectField label="Shoe Size *" value={form.shoe_size} options={SHOE_SIZES} onChange={v => f('shoe_size', v)} />
        </Grid>

        <SectionDivider>Emergency Contact</SectionDivider>
        <Grid>
          <Field label="Contact Name *" value={form.emergency_name} onChange={v => f('emergency_name', v)} />
          <Field label="Phone *" value={form.emergency_phone} onChange={v => f('emergency_phone', v)} type="tel" />
          <Field label="Relationship" value={form.emergency_relationship} onChange={v => f('emergency_relationship', v)} placeholder="Spouse, Parent, etc." />
        </Grid>

        <SectionDivider>Driver's License</SectionDivider>
        <Grid>
          <Field label="DL Number *" value={form.dl_number} onChange={v => f('dl_number', v)} />
          <Field label="Issuing State" value={form.dl_state} onChange={v => f('dl_state', v)} />
          <Field label="Expiration Date *" value={form.dl_expiry} onChange={v => f('dl_expiry', v)} type="date" />
        </Grid>
        <div style={styles.field}>
          <label style={styles.label}>Driver's License Photo *</label>
          <p style={{ color: '#8FA8C0', fontSize: 13, margin: '0 0 10px' }}>Take a clear photo of the front of your driver's license. On mobile, you can use your camera directly.</p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' as const }}>
            <label style={{ background: dlFile ? '#10B98122' : '#4A7EB5', border: dlFile ? '1px solid #10B981' : 'none', color: '#fff', padding: '10px 18px', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
              {dlFile ? `✅ ${dlFile.name}` : '📷 Take / Upload Photo'}
              <input type="file" accept="image/*" capture="environment" onChange={e => setDlFile(e.target.files?.[0] || null)} style={{ display: 'none' }} />
            </label>
            {dlFile && <button onClick={() => setDlFile(null)} style={{ background: 'transparent', border: '1px solid #EF4444', color: '#EF4444', padding: '10px 14px', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>Remove</button>}
          </div>
        </div>

        <NavRow onBack={() => setStep(0)} onNext={handleNext} saving={saving} />
      </div>
    </Screen>
  );

  if (STEPS[step].id === 'payroll') return (
    <Screen step={step}>
      <SectionTitle>Payroll Setup</SectionTitle>
      {validationError && <ValidationError message={validationError} />}
      <div style={styles.card}>
        <div style={styles.payBox}>
          <div style={styles.payTitle}>Your Compensation</div>
          {invite.role === 'salesman' && <>
            <PayLine label="Base Pay" value="$15.00 / hour" />
            <PayLine label="Performance Bonus" value="$300 per qualifying checklist + claim" />
            <PayLine label="Vehicle Allowance" value="$30.00 / active field day" />
          </>}
          {invite.role === 'estimator' && <>
            <PayLine label="Base Pay" value="$15.00 / hour" />
            <PayLine label="Commission" value="Per signed estimate (as configured in the app)" />
          </>}
          {invite.role === 'foreman' && <>
            <PayLine label="Base Pay" value="$20.00 / hour" />
            <PayLine label="Overtime" value="$30.00 / hour after 40 hours/week" />
          </>}
          {invite.role === 'contracting_officer' && <>
            <PayLine label="Base Pay" value="$17.00 / hour" />
            <PayLine label="Work Arrangement" value="Hybrid; weekly hours vary" />
          </>}
          <PayLine label="Pay Schedule" value={invite.role === 'contracting_officer' ? 'Every Tuesday' : 'Weekly — QuickBooks Online Payroll'} />
        </div>

        <SectionDivider>Social Security Number</SectionDivider>
        <p style={{ color: '#8FA8C0', fontSize: 13, marginBottom: 12 }}>Your SSN is required for payroll tax filing. It is submitted directly to QuickBooks Payroll and is never stored in our CRM system.</p>
        <Field label="Social Security Number *" value={form.ssn} onChange={v => f('ssn', v)} type="password" placeholder="XXX-XX-XXXX" full />

        <SectionDivider>Direct Deposit</SectionDivider>
        <p style={{ color: '#8FA8C0', fontSize: 13, marginBottom: 12 }}>Bank information is submitted directly to QuickBooks Payroll and is never stored in our CRM system.</p>
        <Grid>
          <Field label="Bank Routing Number *" value={form.bank_routing} onChange={v => f('bank_routing', v)} type="password" />
          <Field label="Account Number *" value={form.bank_account} onChange={v => f('bank_account', v)} type="password" />
          <SelectField label="Account Type" value={form.bank_type} options={['checking', 'savings']} onChange={v => f('bank_type', v)} />
        </Grid>

        <div style={styles.secureNote}>🔒 Your financial information is encrypted in transit and submitted directly to QuickBooks Payroll. McMullen Browne does not store your SSN or bank account numbers.</div>

        <NavRow onBack={() => setStep(1)} onNext={handleNext} saving={saving} />
      </div>
    </Screen>
  );

  if (STEPS[step].id === 'email') return (
    <Screen step={step}>
      <SectionTitle>Work Email</SectionTitle>
      {validationError && <ValidationError message={validationError} />}
      <div style={styles.card}>
        <p style={styles.cardText}>Choose your McMullen Browne work email address. This will be your official company email for all business communications.</p>
        <div style={styles.field}>
          <label style={styles.label}>Requested Work Email *</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              value={form.work_email_requested}
              onChange={e => { f('work_email_requested', e.target.value); setEmailAvailable(null); }}
              onBlur={() => checkEmail(form.work_email_requested)}
              placeholder="firstname@mcmullenbrowne.com"
              style={{ ...styles.input, flex: 1 }}
            />
            {emailChecking && <span style={{ color: '#8FA8C0', fontSize: 12 }}>Checking...</span>}
            {emailAvailable === true && <span style={{ color: '#10B981', fontSize: 13, fontWeight: 700 }}>✅ Available</span>}
            {emailAvailable === false && <span style={{ color: '#EF4444', fontSize: 13, fontWeight: 700 }}>✗ Taken</span>}
          </div>
          <div style={{ color: '#8FA8C0', fontSize: 12, marginTop: 6 }}>Must end in @mcmullenbrowne.com</div>
        </div>
        <div style={styles.emailTips}>
          <div style={{ fontWeight: 700, color: '#fff', marginBottom: 6 }}>Common formats:</div>
          {(() => {
            const parts = invite.name.toLowerCase().split(' ');
            const first = parts[0]; const last = parts[parts.length - 1];
            return [
              `${first}@mcmullenbrowne.com`,
              `${first}.${last}@mcmullenbrowne.com`,
              `${first[0]}${last}@mcmullenbrowne.com`,
            ].map(s => (
              <div key={s} onClick={() => { f('work_email_requested', s); setEmailAvailable(null); checkEmail(s); }}
                style={{ color: '#4A7EB5', cursor: 'pointer', fontSize: 13, marginBottom: 3 }}>
                {s}
              </div>
            ));
          })()}
        </div>
        <p style={{ color: '#8FA8C0', fontSize: 13 }}>Your work email will be created after your onboarding is reviewed and approved by management. You'll receive setup instructions via your personal email.</p>
        <NavRow onBack={() => setStep(2)} onNext={handleNext} saving={saving} nextDisabled={emailAvailable === false} />
      </div>
    </Screen>
  );

  if (STEPS[step].id === 'contract') return (
    <Screen step={step}>
      <SectionTitle>Employment Contract</SectionTitle>
      {validationError && <ValidationError message={validationError} />}
      <div style={styles.card}>
        <p style={styles.cardText}>Please read your employment contract carefully before signing. Scroll to the bottom to sign.</p>
        {invite.role === 'contracting_officer' && (
          <a href={`${API}/onboarding/document/${token}/agreement`} style={{ display: 'inline-block', background: '#243447', color: '#fff', textDecoration: 'none', fontWeight: 700, fontSize: 13, padding: '9px 18px', borderRadius: 8, margin: '0 0 14px' }}>⬇️ Download the Word agreement</a>
        )}
        {contractHTML ? (
          <iframe
            title="Employment agreement"
            srcDoc={contractHTML}
            sandbox=""
            style={styles.contractFrame}
          />
        ) : (
          <div style={styles.contractLoading}>Loading contract...</div>
        )}

        <SectionDivider>Your Signature</SectionDivider>
        <p style={{ color: '#8FA8C0', fontSize: 13, marginBottom: 12 }}>By signing below, you confirm you have read and agree to all terms of this employment agreement.</p>
        <div style={styles.sigPad}>
          <SignatureCanvas
            ref={sigRef}
            onEnd={() => setSignatureData(sigRef.current?.toDataURL() || '')}
            canvasProps={{ style: { width: '100%', height: 140, background: '#fff' } }}
            backgroundColor="#ffffff"
            penColor="#0F1923"
          />
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button onClick={() => { sigRef.current?.clear(); setSignatureData(''); f('contract_signed', false); }}
            style={{ ...styles.secondaryBtn }}>Clear</button>
          {signatureData && <span style={{ color: '#10B981', fontSize: 13, alignSelf: 'center' }}>✅ Signature captured</span>}
        </div>
        <Checkbox
          label="I have read, understand, and agree to the terms of this Employment Agreement"
          checked={form.contract_signed}
          onChange={v => f('contract_signed', v)}
        />
        <NavRow onBack={() => setStep(3)} onNext={handleNext} saving={saving}
          nextDisabled={!form.contract_signed || !signatureData} />
      </div>
    </Screen>
  );

  if (STEPS[step].id === 'acknowledgments') return (
    <Screen step={step}>
      <SectionTitle>Agreements & Consent</SectionTitle>
      {validationError && <ValidationError message={validationError} />}
      <div style={styles.card}>
        <p style={styles.cardText}>Please read and acknowledge each of the following:</p>

        <AckBox title={invite.role === 'contracting_officer' ? 'Contracting Officer Handbook' : 'Employee Handbook'}>
          <p>{invite.role === 'contracting_officer' ? 'Read the Contracting Officer Handbook before acknowledging. It covers the role, Government CRM and SAM.gov access, hybrid work, security, opportunity preparation, approval limits, training, and performance standards.' : 'Please read the McMullen Browne Employee Handbook before acknowledging. It covers all company policies including attendance, dress code, conduct, social media, and confidentiality standards.'}</p>
          <a href={invite.role === 'contracting_officer' ? `${API}/onboarding/handbook/${token}` : '/handbook'} target="_blank" rel="noreferrer" style={{ display: 'inline-block', background: '#4A7EB5', color: '#fff', textDecoration: 'none', fontWeight: 700, fontSize: 13, padding: '9px 18px', borderRadius: 8, margin: '10px 8px 12px 0' }}>📚 Read the Handbook →</a>
          {invite.role === 'contracting_officer' && <a href={`${API}/onboarding/document/${token}/handbook`} style={{ display: 'inline-block', background: '#243447', color: '#fff', textDecoration: 'none', fontWeight: 700, fontSize: 13, padding: '9px 18px', borderRadius: 8, margin: '10px 0 12px' }}>⬇️ Download Word copy</a>}
          <Checkbox label={`I have read and agree to follow the ${invite.role === 'contracting_officer' ? 'Contracting Officer Handbook' : 'McMullen Browne Employee Handbook'}`} checked={form.handbook_acknowledged} onChange={v => f('handbook_acknowledged', v)} />
        </AckBox>

        <AckBox title="Drug-Free Workplace">
          <p>I understand that McMullen Browne Development LLC maintains a drug-free workplace. I consent to random drug testing as directed by management and understand that a positive result may result in immediate termination.</p>
          <Checkbox label="I acknowledge the Drug-Free Workplace Policy" checked={form.drug_policy} onChange={v => f('drug_policy', v)} />
        </AckBox>

        <AckBox title="Background Check Authorization">
          <p>I authorize McMullen Browne Development LLC to conduct a background check including criminal history, driving record, and identity verification. I understand employment is contingent on a satisfactory result.</p>
          <Checkbox label="I authorize the background check" checked={form.bg_check} onChange={v => f('bg_check', v)} />
        </AckBox>

        <AckBox title="SMS Notifications — Optional">
          <p style={{ color: '#10B981', fontWeight: 700, marginBottom: 8 }}>This is completely optional. Skipping this will not affect your employment or onboarding completion.</p>
          <p>McMullen Browne Development LLC may send internal SMS notifications to employees regarding new customer leads, job updates, and schedule changes. Message frequency varies — typically 1–20 messages per day. <strong>Message and data rates may apply.</strong> You may reply <strong>STOP</strong> at any time to unsubscribe or <strong>HELP</strong> for assistance.</p>
          <p style={{ marginTop: 8 }}>Contact: jarvis@mcmullenbrowne.com | (302) 332-7573</p>
          <p style={{ marginTop: 8 }}>Mobile numbers are never shared with third parties for marketing or promotional purposes. See our <a href="https://mcmullenbrownecrm.com/privacy" style={{ color: '#4A7EB5' }}>Privacy Policy</a> and <a href="https://mcmullenbrownecrm.com/terms" style={{ color: '#4A7EB5' }}>Terms</a>.</p>
          <Checkbox
            label="(Optional) I voluntarily consent to receive internal SMS notifications from McMullen Browne Development LLC at my mobile number. This is not required for employment."
            checked={form.sms_consent}
            onChange={v => f('sms_consent', v)}
          />
        </AckBox>

        <NavRow onBack={() => setStep(4)} onNext={handleNext} saving={saving}
          nextDisabled={!form.handbook_acknowledged || !form.drug_policy || !form.bg_check} />
      </div>
    </Screen>
  );

  if (STEPS[step].id === 'training') return (
    <Screen step={step}>
      <SectionTitle>{invite.role === 'contracting_officer' ? 'Your Initial Training Plan' : 'Your First-Week Training Plan'}</SectionTitle>
      <div style={styles.card}>
        <p style={styles.cardText}>{invite.role === 'contracting_officer' ? 'Your manager will schedule these paid training sessions according to your weekly hours. The plan may span more than one calendar week, and each practical exercise must be reviewed before it is marked complete.' : "Here's what your first week at McMullen Browne looks like. You'll complete these training modules during your onboarding period. Some include quizzes — you'll need 80% or higher to pass."}</p>
        {[1,2,3,4,5].map(day => {
          const dayModules = trainingModules.filter((m: any) => m.day === day);
          if (!dayModules.length) return null;
          return (
            <div key={day} style={styles.trainingDay}>
              <div style={styles.trainingDayHeader}>Day {day}</div>
              {dayModules.map((m: any, i: number) => (
                <div key={i} style={styles.trainingModule}>
                  <div style={styles.trainingIcon}>{m.format === 'presentation' || m.format === 'video' ? '🎤' : m.format === 'quiz' ? '📝' : m.format === 'activity' ? '🔨' : '📖'}</div>
                  <div>
                    <div style={styles.trainingTitle}>{m.title}</div>
                    <div style={styles.trainingMeta}>{m.format} · {m.duration_minutes || m.duration} min{m.quiz_pass_score ? ` · Pass: ${m.quiz_pass_score}%` : ''}</div>
                    {m.description && <div style={styles.trainingDesc}>{m.description}</div>}
                  </div>
                </div>
              ))}
            </div>
          );
        })}
        <p style={{ color: '#8FA8C0', fontSize: 13, marginTop: 16 }}>Your manager will schedule your training sessions. Videos are available in the app. Written guides and quizzes will be sent to your work email once your account is active.</p>
        <NavRow onBack={() => setStep(5)} onNext={handleComplete} saving={saving} nextLabel="Complete Onboarding ✓" />
      </div>
    </Screen>
  );

  // Done
  return (
    <Screen>
      <div style={{ textAlign: 'center', padding: '60px 24px' }}>
        <div style={{ fontSize: 64, marginBottom: 20 }}>🎉</div>
        <div style={{ color: '#fff', fontSize: 26, fontWeight: 900, marginBottom: 4 }}>Onboarding Complete!</div>
        <p style={{ color: '#cbd5e1', fontSize: 16, marginTop: 12, lineHeight: 1.7 }}>
          Welcome to McMullen Browne Development LLC, <strong style={{ color: '#fff' }}>{invite.name}</strong>.<br />
          Management will review your information and reach out within 24 hours with your start details and work email setup.
        </p>
        <div style={{ marginTop: 32, background: '#1A2B3C', borderRadius: 12, padding: 20, border: '1px solid #1E3A5F', maxWidth: 400, margin: '32px auto 0' }}>
          <div style={{ color: '#8FA8C0', fontSize: 12, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>Questions?</div>
          <div style={{ color: '#fff', fontWeight: 700 }}>Sales Manager</div>
          <div style={{ color: '#4A7EB5', fontSize: 14 }}>(302) 332-7573</div>
          <div style={{ color: '#4A7EB5', fontSize: 14 }}>jarvis@mcmullenbrowne.com</div>
        </div>
      </div>
    </Screen>
  );
}

// ─── Sub-Components ───────────────────────────────────────────────────────────

function ValidationError({ message }: { message: string }) {
  return (
    <div style={{ background: '#EF444422', border: '1px solid #EF444466', borderRadius: 10, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{ fontSize: 18 }}>⚠️</span>
      <span style={{ color: '#EF4444', fontSize: 14, fontWeight: 600 }}>{message}</span>
    </div>
  );
}

function Screen({ children, step }: { children: React.ReactNode, step?: number }) {
  const [isMobile, setIsMobile] = React.useState(false);
  React.useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 600);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: '#0F1923', fontFamily: 'Arial, sans-serif' }}>
      <style>{`
        input, select, textarea { font-size: 16px !important; }
        input[type="date"] { text-align: left; min-height: 50px; display: block; -webkit-appearance: none; appearance: none; }
        input[type="date"]::-webkit-date-and-time-value { text-align: left; }
        .onboard-step-tab { padding: 12px 14px; font-size: 11px; font-weight: 600; white-space: nowrap; cursor: default; }
        .onboard-nav-row { display: flex; gap: 12px; margin-top: 28px; }
        .onboard-nav-row button { min-height: 52px; }
      `}</style>

      {/* Sticky Header */}
      <div style={{ background: '#0a1118', borderBottom: '3px solid #4A7EB5', padding: isMobile ? '12px 16px' : '14px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 10 }}>
        <img src="/logo-white.png" alt="McMullen Browne" style={{ height: isMobile ? 28 : 34, width: 'auto', display: 'block' }} />
        {step !== undefined && (
          <div style={{ color: '#4A7EB5', fontSize: 12, fontWeight: 700 }}>Step {step + 1} of {STEPS.length}</div>
        )}
      </div>

      {/* Step Progress Tabs */}
      {step !== undefined && (
        <div style={{ display: 'flex', background: '#0a1118', borderBottom: '1px solid #1E3A5F', overflowX: 'auto', WebkitOverflowScrolling: 'touch' as any }}>
          {STEPS.map((s, i) => (
            <div key={s.id} className="onboard-step-tab" style={{
              color: i === step ? '#4A7EB5' : i < step ? '#10B981' : '#4A6580',
              borderBottom: i === step ? '2px solid #4A7EB5' : i < step ? '2px solid #10B981' : '2px solid transparent',
              paddingLeft: i === 0 ? (isMobile ? 16 : 24) : undefined,
            }}>
              {i < step ? '✓ ' : ''}{s.label}
            </div>
          ))}
        </div>
      )}

      <div style={{ maxWidth: 720, margin: '0 auto', padding: isMobile ? '20px 14px 48px' : '32px 20px' }}>{children}</div>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h2 style={{ color: '#fff', fontSize: 22, fontWeight: 800, marginBottom: 16 }}>{children}</h2>;
}
function SectionDivider({ children }: { children: React.ReactNode }) {
  return <div style={{ color: '#4A7EB5', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, margin: '20px 0 10px', borderTop: '1px solid #1E3A5F', paddingTop: 16 }}>{children}</div>;
}
function Grid({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))', gap: 12, marginBottom: 12 }}>{children}</div>;
}
function Field({ label, value, onChange, type = 'text', placeholder = '', full = false }: { label: string; value: string; onChange: (v: string) => void; type?: string; placeholder?: string; full?: boolean }) {
  return (
    <div style={{ ...(full ? { marginBottom: 12 } : {}) }}>
      <label style={styles.label}>{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={styles.input} />
    </div>
  );
}
function SelectField({ label, value, options, onChange }: { label: string; value: string; options: string[]; onChange: (v: string) => void }) {
  return (
    <div>
      <label style={styles.label}>{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)} style={styles.input}>
        <option value="">Select...</option>
        {options.map((o: string) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
function Checkbox({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', margin: '12px 0 0' }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} style={{ marginTop: 3, width: 16, height: 16, accentColor: '#4A7EB5', flexShrink: 0 }} />
      <span style={{ fontSize: 13, color: '#cbd5e1', lineHeight: 1.6 }}>{label}</span>
    </label>
  );
}
function AckBox({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: '#1A2B3C', border: '1px solid #1E3A5F', borderRadius: 10, padding: 16, marginBottom: 16 }}>
      <div style={{ color: '#4A7EB5', fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>{title}</div>
      <div style={{ color: '#8FA8C0', fontSize: 13, lineHeight: 1.7 }}>{children}</div>
    </div>
  );
}
function PayLine({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: 8, marginBottom: 8, borderBottom: '1px solid #1E3A5F' }}>
      <span style={{ color: '#8FA8C0', fontSize: 13 }}>{label}</span>
      <span style={{ color: '#fff', fontSize: 13, fontWeight: 700, textAlign: 'right' }}>{value}</span>
    </div>
  );
}
function NavRow({ onBack, onNext, saving, nextDisabled = false, nextLabel = 'Save & Continue →' }: { onBack?: () => void; onNext: () => void; saving: boolean; nextDisabled?: boolean; nextLabel?: string }) {
  return (
    <div style={{ display: 'flex', gap: 12, marginTop: 28 }}>
      {onBack && <button onClick={onBack} style={styles.secondaryBtn}>← Back</button>}
      <button onClick={onNext} disabled={saving || nextDisabled} style={{ ...styles.primaryBtn, opacity: (saving || nextDisabled) ? 0.5 : 1, flex: 1 }}>
        {saving ? 'Saving...' : nextLabel}
      </button>
    </div>
  );
}
function Btn({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return <button onClick={onClick} style={{ ...styles.primaryBtn, width: '100%', marginTop: 20 }}>{children}</button>;
}

const styles: Record<string, React.CSSProperties> = {
  loading: { color: '#8FA8C0', textAlign: 'center', padding: 60, fontSize: 16 },
  errorBox: { background: '#EF444422', border: '1px solid #EF4444', borderRadius: 10, padding: 20, color: '#EF4444', fontSize: 14 },
  hero: { textAlign: 'center', padding: '32px 0 20px' },
  heroLogo: { display: 'none' },
  heroSub: { color: '#4A7EB5', fontSize: 12, textTransform: 'uppercase', letterSpacing: 2, marginTop: 6 },
  card: { background: '#1A2B3C', borderRadius: 16, border: '1px solid #1E3A5F', padding: '20px 18px' },
  cardTitle: { color: '#fff', fontSize: 22, fontWeight: 800, marginBottom: 12 },
  cardText: { color: '#8FA8C0', fontSize: 15, lineHeight: 1.8, marginBottom: 14 },
  list: { color: '#cbd5e1', fontSize: 15, lineHeight: 2.2, paddingLeft: 20 },
  field: { marginBottom: 12 },
  label: { display: 'block', fontSize: 12, fontWeight: 700, color: '#8FA8C0', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 },
  input: { width: '100%', padding: '13px 14px', borderRadius: 10, background: '#243447', border: '1px solid #2D4259', color: '#fff', fontSize: 16, boxSizing: 'border-box' as const },
  primaryBtn: { background: '#4A7EB5', color: '#fff', border: 'none', borderRadius: 12, padding: '15px 24px', fontWeight: 700, fontSize: 16, cursor: 'pointer' },
  secondaryBtn: { background: '#243447', color: '#8FA8C0', border: '1px solid #2D4259', borderRadius: 12, padding: '15px 20px', fontWeight: 600, fontSize: 15, cursor: 'pointer' },
  payBox: { background: '#0F1923', borderRadius: 10, border: '1px solid #1E3A5F', padding: 16, marginBottom: 20 },
  payTitle: { color: '#4A7EB5', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 },
  secureNote: { background: '#10B98111', border: '1px solid #10B98133', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#10B981', marginTop: 16 },
  emailTips: { background: '#0F1923', border: '1px solid #1E3A5F', borderRadius: 8, padding: 14, marginTop: 8, marginBottom: 16 },
  contractFrame: { background: '#fff', border: 'none', borderRadius: 8, width: '100%', height: 650, display: 'block', marginBottom: 16 },
  contractLoading: { background: '#fff', color: '#1c2228', borderRadius: 8, padding: 24, minHeight: 120, fontSize: 13, marginBottom: 16 },
  sigPad: { border: '2px solid #1E3A5F', borderRadius: 10, overflow: 'hidden', background: '#fff' },
  trainingDay: { marginBottom: 20 },
  trainingDayHeader: { color: '#4A7EB5', fontWeight: 800, fontSize: 14, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10, padding: '6px 0', borderBottom: '1px solid #1E3A5F' },
  trainingModule: { display: 'flex', gap: 12, alignItems: 'flex-start', background: '#0F1923', borderRadius: 8, padding: 12, marginBottom: 8, border: '1px solid #1E3A5F' },
  trainingIcon: { fontSize: 20, flexShrink: 0, marginTop: 2 },
  trainingTitle: { color: '#fff', fontWeight: 700, fontSize: 14 },
  trainingMeta: { color: '#4A7EB5', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 2 },
  trainingDesc: { color: '#8FA8C0', fontSize: 12, marginTop: 4, lineHeight: 1.5 },
};
